import HowItWorksPage from './pages/HowItWorks';
import QuoteRequest from './pages/QuoteRequest';
import AboutPage from './pages/About';
import ServicesPage from './pages/Services';
import './App.css';
// src/App.jsx
import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import {
  Package, Truck, MapPin, Clock, Users,
  Mail, Phone, MapPinned, X, ChevronDown,
  MessageCircle, X as CloseIcon, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  Routes,
  Route,
  Link, useLocation} from 'react-router-dom';

// Import pages & components
import QuickQuote from './pages/QuickQuote';
import Contact from './pages/Contact';
import ManWithVan from './pages/ManWithVan';
import Header from './components/Header';
import Testimonials from './components/Testimonials';
import useGoogleAnalytics from './utils/useGoogleAnalytics';
import { track } from './utils/track';

import {
  GlobalStyle, Container,
  Hero, VideoBackground, HeroContent, HeroBox, HeroTitle, HeroSubtitle, HeroButtons,
  AboutSection, AboutContainer, AboutText, AboutTitle, AboutDescription, MetricsGrid, MetricCard,
  Section, SectionTitle, SectionSubtitle, ServicesGrid, ServiceCard, ServiceIcon, ServiceTitle, ServiceDescription,
  HowItWorksSection, HowItWorksContainer, StepsGrid, StepCard, StepNumber, StepH3, StepDescription,
  CTASection, CTAContent, CTATitle, CTAText, CTAButtonLarge, CTAButtonTwo,
  FormGroup, FormLabel, FormInput, FormTextarea,
  Footer, FooterContent, FooterColumn, Copyright,
  CTAButton
} from './styles';

// ─────────────────────────────────────────────────────────────────────────────
// DETERMINISTIC PRNG (Mulberry32)
// ─────────────────────────────────────────────────────────────────────────────
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = a ^ (a >>> 15);
    t = (t * 0x1f873d ^ t) | 0;
    return ((t ^ (t >>> 7)) >>> 0) / 2147483647;
  };
}

const DELIVERIES_SEED = 0xabcdef12;
const ONTIME_SEED     = 0xdeadbeef;

function calculateLiveMetrics() {
  const rng = mulberry32(DELIVERIES_SEED);
  const today = new Date(); today.setUTCHours(0,0,0,0);
  const now = new Date();
  const currentHour = now.getUTCHours();
  const currentSlotStart = new Date(now);
  currentSlotStart.setUTCHours(Math.floor(currentHour/2)*2,0,0,0);

  let deliveries = 1500, clients = 100, dailyClients = 0;
  const cur = new Date(today);

  while (cur < currentSlotStart) {
    const isWeekend = cur.getDay() === 0 || cur.getDay() === 6;
    if (!isWeekend) {
      const slotAdd = 7 + Math.floor(rng()*12);
      deliveries += slotAdd;
      if (dailyClients < 1 && rng() > 0.7) { clients++; dailyClients++; }
    }
    cur.setUTCHours(cur.getUTCHours()+2);
  }
  return { deliveries, clients };
}

function useLiveMetrics() {
  const [metrics, setMetrics] = useState(calculateLiveMetrics);

  useEffect(() => {
    const tick = () => {
      const today = new Date();
      if (today.getDay() === 0 || today.getDay() === 6) return;
      setMetrics(calculateLiveMetrics);
    };
    const id = setInterval(tick, 2 * 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return metrics;
}

const LiveCounter = ({ label, end, suffix = '', decimals = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <MetricCard whileHover={{ scale: 1.05 }}>
      <h3 ref={ref}>
        {inView ? <CountUp end={end} duration={2.5} decimals={decimals} suffix={suffix} /> : '0'}
      </h3>
      <p>{label}</p>
    </MetricCard>
  );
};

// WhatsApp Widget Component
const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const phoneNumber = '447436117842';
  
  const handleOpenChat = () => {
    const savedName = localStorage.getItem('whatsapp_name');
    if (savedName) setName(savedName);
    setIsOpen(!isOpen);
  };

  const handleSendMessage = () => {
    if (name) localStorage.setItem('whatsapp_name', name);

    let finalMessage = message.trim();
    if (name) {
      finalMessage += `\n\n---\nName: ${name}`;
    }

    const encodedMessage = encodeURIComponent(finalMessage);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    window.open(whatsappURL, '_blank');
    setMessage('');
    setIsOpen(false);
    
    if (window.gtag) {
      window.gtag('event', 'whatsapp_click', {
        'event_category': 'engagement',
        'event_label': 'custom_message'
      });
    }
  };

  const getWidgetWidth = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 'calc(100vw - 40px)';
      if (window.innerWidth < 768) return '350px';
      return '380px';
    }
    return '380px';
  };

  return (
    <>
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
        }}
      >
        <motion.button
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleOpenChat}
          style={{
            background: 'linear-gradient(135deg, #25D366, #128C7E)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
            position: 'relative',
          }}
          aria-label="Open WhatsApp chat"
        >
          <MessageCircle size={30} />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1, 1, 0] }}
            transition={{ repeat: Infinity, duration: 2, delay: 1 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '50%',
              border: '2px solid #25D366',
              zIndex: -1,
            }}
          />
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            style={{
              position: 'fixed',
              bottom: '90px',
              right: '20px',
              width: getWidgetWidth(),
              maxWidth: 'calc(100vw - 40px)',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              zIndex: 9998,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              margin: '0 10px',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #25D366, #128C7E)',
              color: 'white',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <div style={{
                  background: 'white',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <MessageCircle size={24} color="#25D366" />
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', lineHeight: '1.3' }}>WhatsApp Support</h4>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.9, lineHeight: '1.3' }}>Typically replies in minutes</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginLeft: '10px',
                }}
                aria-label="Close chat"
              >
                <CloseIcon size={18} />
              </button>
            </div>

            <div style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'none',
                    minHeight: '120px',
                    marginBottom: '15px',
                    boxSizing: 'border-box',
                  }}
                />
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  style={{
                    width: '100%',
                    background: message.trim() ? 'linear-gradient(135deg, #25D366, #128C7E)' : '#cbd5e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '14px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: message.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxSizing: 'border-box',
                  }}
                >
                  <MessageCircle size={18} />
                  Send via WhatsApp
                </motion.button>
              </div>

              <p style={{ 
                fontSize: '11px', 
                color: '#94a3b8', 
                textAlign: 'center', 
                margin: '20px 0 0 0',
                lineHeight: '1.4',
                padding: '0 5px',
              }}>
                By contacting us, you agree to our Privacy Policy. 
                WhatsApp will open in a new tab.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(2px)',
            zIndex: 9997,
          }}
        />
      )}
    </>
  );
};

const HAMPSHIRE_AREAS = [
  'Alton','Andover','Basingstoke','Eastleigh','Fareham','Farnborough','Fleet',
  'Gosport','Havant','Lymington','New Milton','Petersfield','Portsmouth',
  'Ringwood','Romsey','Southampton','Waterlooville','Winchester','Yateley'
].sort();

const LocationSelect = ({
  label, value, onChange, required = false, onCitySelect, error = false, helperText = ''
}) => {
  const [input, setInput] = useState(value);
  const [filtered, setFiltered] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (input) {
      const lower = input.toLowerCase();
      const matches = HAMPSHIRE_AREAS.filter(a => a.toLowerCase().includes(lower)).slice(0,6);
      setFiltered(matches);
      setShowDropdown(matches.length > 0);
    } else {
      setFiltered([]);
      setShowDropdown(false);
    }
  }, [input]);

  const selectItem = (item) => {
    setInput(item);
    onChange(item);
    onCitySelect?.(item);
    setShowDropdown(false);
  };

  return (
    <FormGroup style={{position:'relative'}}>
      <FormLabel>
        {label} {required && <span style={{color:'#e00'}}>*</span>}
      </FormLabel>
      <FormInput
        type="text"
        value={input}
        onChange={e => { setInput(e.target.value); onChange(e.target.value); }}
        onFocus={() => input && setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        required={required}
        placeholder="Start typing a Hampshire town..."
        style={{ borderColor: error ? '#e00' : undefined }}
      />
      {helperText && (
        <p style={{margin:'4px 0 0', fontSize:'0.75rem', color:'#e00'}}>{helperText}</p>
      )}
      {showDropdown && (
        <motion.ul
          initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}}
          style={{
            position:'absolute', top:'100%', left:0, right:0,
            background:'white', border:'1px solid #ddd', borderRadius:'6px',
            marginTop:'4px', maxHeight:'180px', overflowY:'auto',
            zIndex:10, listStyle:'none', padding:0, margin:0,
            boxShadow:'0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          {filtered.map((area,i) => (
            <li
              key={i}
              onMouseDown={(e) => {
                e.stopPropagation();
                selectItem(area);
              }}
              style={{
                padding:'0.5rem 0.75rem', cursor:'pointer',
                borderBottom: i < filtered.length-1 ? '1px solid #eee' : 'none'
              }}
              whileHover={{backgroundColor:'#f0f7ff'}}
            >
              {area}
            </li>
          ))}
        </motion.ul>
      )}
    </FormGroup>
  );
};

const AddressFields = ({
  title, street, setStreet, line2, setLine2, postcode, setPostcode, city, setCity,
  cityInputProps = {}
}) => {
  const { onCitySelect, error, helperText } = cityInputProps;

  return (
    <>
      <FormGroup>
        <FormLabel>{title} - Street Number & Name <span style={{color:'#e00'}}>*</span></FormLabel>
        <FormInput type="text" value={street} onChange={e => setStreet(e.target.value)} required />
      </FormGroup>

      <FormGroup>
        <FormLabel>{title} - Address Line 2 (Optional)</FormLabel>
        <FormInput type="text" value={line2} onChange={e => setLine2(e.target.value)} />
      </FormGroup>

      <FormGroup>
        <FormLabel>{title} - Postcode <span style={{color:'#e00'}}>*</span></FormLabel>
        <FormInput type="text" value={postcode} onChange={e => setPostcode(e.target.value)} required />
      </FormGroup>

      <LocationSelect
        label={`${title} - City/Town`}
        value={city}
        onChange={setCity}
        required
        onCitySelect={onCitySelect}
        error={error}
        helperText={helperText}
      />
    </>
  );
};

const QuoteModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const [pickupCity, setPickupCity] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [pickupStreet, setPickupStreet] = useState('');
  const [pickupLine2, setPickupLine2] = useState('');
  const [pickupPostcode, setPickupPostcode] = useState('');
  const [dropoffStreet, setDropoffStreet] = useState('');
  const [dropoffLine2, setDropoffLine2] = useState('');
  const [dropoffPostcode, setDropoffPostcode] = useState('');
  const [service, setService] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [packageDetails, setPackageDetails] = useState('');
  const [urgent, setUrgent] = useState('');
  const [collectionTime, setCollectionTime] = useState('');
  const [earliestDelivery, setEarliestDelivery] = useState('');
  const [stops, setStops] = useState('');
  const [csv, setCsv] = useState('');

  useEffect(() => {
    const handleClickOutside = e => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const timeSlots = [];
  for (let h = 8; h <= 18; h++) {
    for (let m = 0; m < 60; m += 30) {
      const t = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      timeSlots.push(t);
    }
  }

  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const isTimeDisabled = (slot) => {
    const [h, m] = slot.split(':').map(Number);
    return (h * 60 + m) < currentTime;
  };

  const serviceOptions = [
    {value:'',label:'Select a service'},
    {value:'same-day',label:'Same Day Delivery'},
    {value:'overnight',label:'Overnight Deliveries'},
    {value:'multi-drop',label:'Multi-Drop Deliveries'},
    {value:'man-with-van',label:'Man with a Van'},
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const pickupOk = HAMPSHIRE_AREAS.includes(pickupCity);
    const dropOk   = HAMPSHIRE_AREAS.includes(deliveryCity);
    if (!pickupOk || !dropOk) {
      alert('Both pickup and drop-off must be a Hampshire town from the dropdown list.');
      return;
    }
    alert('Quote requested! We\'ll be in touch within 1 hour.');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
          style={{
            position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.75)',
            display:'flex', alignItems:'center', justifyContent:'center',
            zIndex:2000, padding:'1rem',
          }}
        >
          <motion.div
            ref={modalRef}
            initial={{scale:0.85,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.85,opacity:0}}
            style={{
              background:'white', borderRadius:'16px', padding:'2rem',
              width:'100%', maxWidth:'620px', maxHeight:'90vh', overflowY:'auto',
              position:'relative',
            }}
          >
            <button onClick={onClose}
              style={{position:'absolute',top:'0.75rem',right:'0.75rem',
                      background:'none',border:'none',cursor:'pointer'}}>
              <X size={28}/>
            </button>

            <h2 style={{marginTop:0,fontSize:'1.8rem',color:'#1a1a1a'}}>Get a Free Quote</h2>
            <p style={{color:'#555',marginBottom:'1.5rem'}}>Fill in your details and we'll get back to you within 1 hour.</p>

            <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:'0rem'}}>
              <FormGroup>
                <FormLabel>Service Type <span style={{color:'#e00'}}>*</span></FormLabel>
                <div style={{position:'relative'}}>
                  <select value={service} onChange={e=>setService(e.target.value)} required
                    style={{appearance:'none', width:'100%', padding:'0.75rem 2.5rem 0.75rem 0.75rem',
                            borderRadius:'8px', border:'1px solid #ccc', fontSize:'1rem', background:'white'}}>
                    {serviceOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <ChevronDown size={20} style={{position:'absolute',right:'0.75rem',top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:'#555'}}/>
                </div>
              </FormGroup>

              {service === 'man-with-van' ? (
                <div style={{
                  padding:'1rem', background:'#fff7ed', border:'1px solid #fdba74',
                  borderRadius:'8px', marginTop:'0.5rem', color:'#7c2d12'
                }}>
                  <p style={{margin:0}}>
                    Man with a Van jobs are quoted individually — this quick quote form doesn't cover it.
                    Visit our{' '}
                    <Link to="/man-with-van" onClick={onClose} style={{color:'#c2410c',fontWeight:600}}>
                      Man with a Van page
                    </Link>{' '}
                    to get in touch and we'll sort you a price.
                  </p>
                </div>
              ) : (
                <>
                  <FormGroup><FormLabel>Name <span style={{color:'#e00'}}>*</span></FormLabel><FormInput type="text" value={name} onChange={e=>setName(e.target.value)} required/></FormGroup>
                  <FormGroup><FormLabel>Email <span style={{color:'#e00'}}>*</span></FormLabel><FormInput type="email" value={email} onChange={e=>setEmail(e.target.value)} required/></FormGroup>
                  <FormGroup><FormLabel>Phone <span style={{color:'#e00'}}>*</span></FormLabel><FormInput type="tel" value={phone} onChange={e=>setPhone(e.target.value)} required/></FormGroup>

                  <div style={{borderTop:'1px solid #eee', paddingTop:'1rem', marginTop:'1rem'}}>
                    <h4 style={{margin:'0 0 0.75rem', fontSize:'1.1rem', color:'#1a1a1a'}}>Pickup Address</h4>
                    <AddressFields title="Pickup" street={pickupStreet} setStreet={setPickupStreet}
                      line2={pickupLine2} setLine2={setPickupLine2} postcode={pickupPostcode} setPostcode={setPickupPostcode}
                      city={pickupCity} setCity={setPickupCity}
                      cityInputProps={{onCitySelect:setPickupCity,
                        error:!HAMPSHIRE_AREAS.includes(pickupCity) && pickupCity!=='',
                        helperText:!HAMPSHIRE_AREAS.includes(pickupCity) && pickupCity!=='' ? 'Please choose a town from the list (Hampshire only)' : ''}} />
                  </div>

                  <div style={{borderTop:'1px solid #eee', paddingTop:'1rem', marginTop:'1rem'}}>
                    <h4 style={{margin:'0 0 0.75rem', fontSize:'1.1rem', color:'#1a1a1a'}}>Drop-off Address</h4>
                    <AddressFields title="Drop-off" street={dropoffStreet} setStreet={setDropoffStreet}
                      line2={dropoffLine2} setLine2={setDropoffLine2} postcode={dropoffPostcode} setPostcode={setDropoffPostcode}
                      city={deliveryCity} setCity={setDeliveryCity}
                      cityInputProps={{onCitySelect:setDeliveryCity,
                        error:!HAMPSHIRE_AREAS.includes(deliveryCity) && deliveryCity!=='',
                        helperText:!HAMPSHIRE_AREAS.includes(deliveryCity) && deliveryCity!=='' ? 'Please choose a town from the list (Hampshire only)' : ''}} />
                  </div>

                  {service === 'same-day' && (
                    <FormGroup>
                      <FormLabel>Urgency</FormLabel>
                      <select value={urgent} onChange={e=>setUrgent(e.target.value)} style={{width:'100%',padding:'0.5rem',borderRadius:'6px',border:'1px solid #ccc'}}>
                        <option value="">Select</option>
                        <option>Within 1 hour</option>
                        <option>2-3 hour window</option>
                      </select>
                    </FormGroup>
                  )}

                  {service === 'overnight' && (
                    <>
                      <FormGroup>
                        <FormLabel>Preferred Collection Time Today</FormLabel>
                        <select value={collectionTime} onChange={e=>setCollectionTime(e.target.value)} style={{width:'100%',padding:'0.5rem',borderRadius:'6px',border:'1px solid #ccc'}}>
                          <option value="">Select time slot</option>
                          {timeSlots.map(slot => (
                            <option key={slot} value={slot} disabled={isTimeDisabled(slot)}>
                              {slot} {isTimeDisabled(slot) && '(Unavailable)'}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Earliest Delivery Tomorrow</FormLabel>
                        <select value={earliestDelivery} onChange={e=>setEarliestDelivery(e.target.value)} style={{width:'100%',padding:'0.5rem',borderRadius:'6px',border:'1px solid #ccc'}}>
                          <option value="">Select</option>
                          <option>Before 9 AM</option>
                          <option>Before 12 PM</option>
                          <option>Anytime</option>
                        </select>
                      </FormGroup>
                    </>
                  )}

                  {service === 'multi-drop' && (
                    <>
                      <FormGroup>
                        <FormLabel>Number of Stops <span style={{color:'#e00'}}>*</span></FormLabel>
                        <FormInput type="number" min="2" value={stops} onChange={e=>setStops(e.target.value)} required />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Paste Addresses (Hampshire only)</FormLabel>
                        <FormTextarea rows={3} value={csv} onChange={e=>setCsv(e.target.value)} placeholder="One address per line..." />
                      </FormGroup>
                    </>
                  )}

                  <FormGroup style={{marginTop:'1rem'}}>
                    <FormLabel>Package Details <span style={{color:'#e00'}}>*</span></FormLabel>
                    <FormTextarea required rows={4} value={packageDetails} onChange={e=>setPackageDetails(e.target.value)} placeholder="Dimensions, weight, contents, special instructions..." />
                  </FormGroup>

                  <CTAButton type="submit" style={{width:'100%',marginTop:'1rem'}} whileHover={{scale:1.02}} whileTap={{scale:0.98}}>
                    Request Quote
                  </CTAButton>
                </>
              )}

              <p style={{textAlign:'center',marginTop:'1rem',fontSize:'0.85rem',color:'#888'}}>
                Powered by <a href="https://draftpoint.co.uk" target="_blank" rel="noopener noreferrer" style={{color:'#001F3D',textDecoration:'none'}}>Draftpoint</a>
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const recentMoves = [
  { id: 1, title: 'Furniture Move', from: 'Basingstoke', to: 'Basingstoke', cost: '60', saved: '22', img: '/customer1.webp', completionTime: '1 hour' },
  { id: 2, title: 'Home Removal', from: "Canning Town", to: 'Canary Wharf', cost: '640', saved: '377', img: '/customer6.webp', completionTime: '6 hours' },
  { id: 3, title: 'Flat Move & Storage', from: 'Canning Town', to: 'Croydon', cost: '450', saved: '45', img: '/customer8.webp', completionTime: '6 hours' },
  { id: 4, title: 'Furniture Move', from: 'Portsmouth', to: 'Portsmouth', cost: '45', saved: '80', img: '/customer7.webp', completionTime: '1 hour' },
];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────

// Automatically scroll to the top when navigating to a new page
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
};

function App() {
  const [quoteModal, setQuoteModal] = useState(false);

  const carouselRef = useRef(null);
  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };
  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };


  const { deliveries, clients } = useLiveMetrics();
  const onTimeRng = mulberry32(ONTIME_SEED);
  const onTimeRate = Math.min(98, Math.max(95, 95 + Math.floor(onTimeRng() * 4)));

  useEffect(() => {
    const handler = () => setQuoteModal(true);
    window.addEventListener('openQuoteModal', handler);
    return () => window.removeEventListener('openQuoteModal', handler);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('tel:')) track('phone_click');
      else if (href.includes('wa.me')) track('whatsapp_click');
      else if (href.startsWith('mailto:')) track('email_click');
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <Container>
      <GlobalStyle />

      <style>{`
        .responsive-footer {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 4rem;
          width: 100%;
        }
        @media (max-width: 768px) {
          .responsive-footer {
            grid-template-columns: 1fr;
            gap: 2rem;
            text-align: center;
          }
          .responsive-footer p {
            justify-content: center;
          }
        }
      `}</style>

      <QuoteModal isOpen={quoteModal} onClose={() => setQuoteModal(false)} />
      
      <WhatsAppWidget />

      <ScrollToTop />
      <Routes>
        <Route path="/" element={
          <>
            <Header />
            {/* HOMEPAGE HERO - CLICKABLE SERVICE CARDS */}
            <section className="home-service-hero">
              <div className="home-service-hero-inner">

                <motion.div
                  className="home-service-hero-copy"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  <h1>Your Delivery &amp; Moving Partner</h1>

                  <p>
                    From urgent business deliveries to house moves and
                    furniture transport, Urgent Logistics makes moving
                    things simple. Reliable, flexible services across
                    the UK, with straightforward quotes
                    to get you moving.
                  </p>

                  <div className="home-service-hero-buttons">
                    <CTAButton as={Link} to="/get-a-quote">
                      Quick Quote
                    </CTAButton>
                    <CTAButtonTwo
                      as={Link}
                      to="/contact"
                      style={{ textDecoration: 'none' }}
                    >
                      Contact Us
                    </CTAButtonTwo>
                  </div>

                  <div className="home-service-hero-rating">
                    <img
                      src="/google_favicon.webp"
                      alt="Google"
                    />
                    <strong>5.0</strong>
                    <span aria-label="5 out of 5 stars">★★★★★</span>
                    
                  </div>
                </motion.div>

                <motion.div
                  className="home-service-hero-grid"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7, delay: 0.15 }}
                >
                  <Link
                    className="home-service-card"
                    to="/get-a-quote?service=business"
                  >
                    <img
                      src="/courier_services.webp"
                      alt="Courier and parcel delivery services"
                    />
                    <div className="home-service-card-label">
                      <span>Business Services</span>
                      <span aria-hidden="true">→</span>
                    </div>
                  </Link>

                  <Link
                    className="home-service-card"
                    to="/get-a-quote?service=house"
                  >
                    <img
                      src="/man_with_van.webp"
                      alt="Man with van and home removals"
                    />
                    <div className="home-service-card-label">
                      <span>House &amp; Flat Moves</span>
                      <span aria-hidden="true">→</span>
                    </div>
                  </Link>

                  <Link
                    className="home-service-card home-service-card-wide"
                    to="/get-a-quote?service=large"
                  >
                    <img
                      src="/other_services.webp"
                      alt="Large item delivery and furniture transport"
                    />
                    <div className="home-service-card-label">
                      <span>Large Item Deliveries</span>
                      <span aria-hidden="true">→</span>
                    </div>
                  </Link>
                </motion.div>

              </div>
            </section>

            {/* RECENT MOVES & REVIEWS (Added under Hero) */}
            <div style={{ background: '#F8F7F4', padding: '5rem 0', overflow: 'hidden' }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4rem',
                alignItems: 'flex-start',
                paddingLeft: 'max(5%, calc((100vw - 1400px) / 2))',
                width: '100%'
              }}>
                {/* Left Column */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  style={{ flex: '1 1 400px', maxWidth: '500px', paddingLeft: '1rem', paddingRight: '2rem', paddingTop: '1.5rem' }}
                >
                  <h2 style={{ fontSize: '2.5rem', color: '#10243D', marginBottom: '1.5rem', lineHeight: '1.2', fontWeight: '800' }}>
                    Recent Moves &amp; Reviews
                  </h2>
<p style={{ fontSize: '1.1rem', color: '#10243D', marginBottom: '1.25rem', lineHeight: '1.7' }}>
                    From local furniture pickups to long-distance relocations, we handle jobs of all sizes. Our transparent pricing means you know exactly what you&apos;re paying upfront no hidden fees, just great service.
                  </p>
                  <p style={{ fontSize: '1.1rem', color: '#10243D', marginBottom: 0, lineHeight: '1.7' }}>
                    Available 24/7, we work around your schedule. Whether you need a last-minute office clearance or secure storage during a house move, our team is ready to help whenever you are.
                  </p>
                </motion.div>

                {/* Right Column: Carousel */}
                <div style={{ flex: '2 1 700px', minWidth: 0 }}>
                  <div
                    ref={carouselRef}
                    onScroll={(e) => {
                      const video = e.currentTarget.querySelector('video');
                      if (video && !video.paused) video.pause();
                    }}
                    style={{
                      display: 'flex',
                      gap: '1.5rem',
                      overflowX: 'auto',
                      scrollSnapType: 'x mandatory',
                      paddingBottom: '1.5rem',
                      scrollbarWidth: 'none',
                      WebkitOverflowScrolling: 'touch',
                      paddingRight: '5%',
                    }}
                  >
                    
                    {/* Video Testimonial */}
                    <div
                      style={{
                        minWidth: '340px',
                        flex: '0 0 auto',
                        background: '#000',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                        scrollSnapAlign: 'start',
                        position: 'relative',
                        minHeight: '420px'
                      }}
                    >
                      <video
                        src="/customer_new.mp4"
                        poster="/customer_new_poster.jpg"
                        controls
                        playsInline
                        preload="metadata"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>

                    {recentMoves.map((move) => (
                      <div
                        key={move.id}
                        style={{
                          minWidth: '340px',
                          flex: '0 0 auto',
                          background: '#fff',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0',
                          overflow: 'hidden',
                          scrollSnapAlign: 'start',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <div style={{ padding: '1rem 1rem 0 1rem' }}>
                          <img 
                            src={move.img} 
                            alt={move.title} 
                            style={{ 
                              width: '100%', 
                              height: '200px',
                              objectFit: 'cover', 
                              borderRadius: '12px', 
                              border: '1px solid #e2e8f0',
                              display: 'block'
                            }} 
                          />
                        </div>
                        
                        <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <h3 style={{ fontSize: '1.4rem', color: '#1a1a1a', marginBottom: '1rem', fontWeight: '700' }}>
                            {move.title}
                          </h3>
                          <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '1.05rem' }}>From: <strong style={{ color: '#1a1a1a' }}>{move.from}</strong></p>
                          <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '1.05rem' }}>To: <strong style={{ color: '#1a1a1a' }}>{move.to}</strong></p>
                          <p style={{ margin: "0 0 0.5rem", color: "#475569", fontSize: "1.05rem" }}>Completion Time: <strong style={{ color: "#1a1a1a" }}>{move.completionTime}</strong></p>
                          <p style={{ margin: 0, color: '#475569', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                            Review: <span style={{ color: '#fbbc04', fontSize: '1.2rem', letterSpacing: '2px' }}>★★★★★</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Carousel Controls */}
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-start', paddingLeft: '1rem' }}>
                    <button
                      onClick={scrollLeft}
                      style={{ width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10243D', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={scrollRight}
                      style={{ width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10243D', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <AboutSection id="about">
              <AboutContainer>
                <AboutText initial={{opacity:0,x:-50}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.8}}>
                  <AboutTitle>YOUR TRUSTED UK-WIDE DELIVERY PARTNER</AboutTitle>
                  <AboutDescription>
                    Urgent Logistics provides reliable courier, transport and removals services across the UK. 
                    Same-day, overnight, multi-drop, and UK-wide solutions.
                  </AboutDescription>
                </AboutText>

                <MetricsGrid initial={{opacity:0,x:50}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.8}}>
                  <LiveCounter label="Deliveries Available" end={24} suffix="/7"/>
                  <LiveCounter label="Happy Clients" end={clients} suffix="+"/>
                  <LiveCounter label="Deliveries Made" end={deliveries} suffix="+"/>
                  <LiveCounter label="On-Time Rate" end={onTimeRate} suffix="%"/>
                </MetricsGrid>
              </AboutContainer>
            </AboutSection>

            <Section services id="services">
              <SectionTitle initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} style={{color:'#1a1a1a'}}>Our Services</SectionTitle>
              <SectionSubtitle initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} style={{color:'#1a1a1a'}}>
                Fast, flexible and reliable delivery solutions across the UK
              </SectionSubtitle>

              <ServicesGrid>
                <ServiceCard whileHover={{scale:1.05}} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}}>
                  <ServiceIcon
                    style={{
                      background: 'transparent',
                      padding: 0,
                      overflow: 'hidden',
                      width: '85%',
                      height: '140px',
                      margin: '0 auto 0.5rem auto'
                    }}
                  >
                    <img
                      src="/usl_multi.webp"
                      alt="Multi-drop delivery service"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </ServiceIcon>
                  <ServiceTitle>Multi-Drop Deliveries</ServiceTitle>
                  <ServiceDescription>Efficient multi-stop delivery solutions for businesses with multiple collection and delivery points across the UK.</ServiceDescription>
                </ServiceCard>

                <ServiceCard whileHover={{scale:1.05}} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.1}}>
                  <ServiceIcon
                    style={{
                      background: 'transparent',
                      padding: 0,
                      overflow: 'hidden',
                      width: '85%',
                      height: '140px',
                      margin: '0 auto 0.5rem auto'
                    }}
                  >
                    <img
                      src="/Clock.webp"
                      alt="Overnight delivery service"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </ServiceIcon>
                  <ServiceTitle>Overnight Deliveries</ServiceTitle>
                  <ServiceDescription>Collect today, deliver tomorrow. Reliable overnight delivery with flexible collections and nationwide coverage.</ServiceDescription>
                </ServiceCard>

                <ServiceCard whileHover={{scale:1.05}} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.2}}>
                  <ServiceIcon
                    style={{
                      background: 'transparent',
                      padding: 0,
                      overflow: 'hidden',
                      width: '85%',
                      height: '140px',
                      margin: '0 auto 0.5rem auto'
                    }}
                  >
                    <img
                      src="/bbc.webp"
                      alt="Business bulk collections service"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </ServiceIcon>
                  <ServiceTitle>Business Bulk Collections</ServiceTitle>
                  <ServiceDescription>Daily or weekly scheduled pickups for e-commerce, retail & offices. Fixed routes, lower rates.</ServiceDescription>
                </ServiceCard>

                <ServiceCard whileHover={{scale:1.05}} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.3}}>
                  <ServiceIcon
                    style={{
                      background: 'transparent',
                      padding: 0,
                      overflow: 'hidden',
                      width: '85%',
                      height: '140px',
                      margin: '0 auto 0.5rem auto'
                    }}
                  >
                    <img
                      src="/same_day.webp"
                      alt="Same day delivery service"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </ServiceIcon>
                  <ServiceTitle>Same Day Delivery</ServiceTitle>
                  <ServiceDescription>Urgent? We've got you covered. Same-day courier service for time-critical shipments.</ServiceDescription>
                </ServiceCard>

                {/* HOME & FLAT MOVES */}
                <ServiceCard whileHover={{scale:1.05}}
                  initial={{opacity:0,y:30}}
                  whileInView={{opacity:1,y:0}}
                  viewport={{once:true}}
                  transition={{delay:0.4}}
                  style={{textDecoration:'none',color:'inherit'}}
                >
                  <ServiceIcon
                    style={{
                      background: 'transparent',
                      padding: 0,
                      overflow: 'hidden',
                      width: '85%',
                      height: '140px',
                      margin: '0 auto 0.5rem auto'
                    }}
                  >
                    <img
                      src="/sofa_new.webp"
                      alt="House and flat moving service"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </ServiceIcon>
                  <ServiceTitle>House &amp; Flat Moves</ServiceTitle>
                  <ServiceDescription>
                    Moving home doesn't have to be stressful.
                    From studio flats to family homes, we provide
                    reliable, flexible moving services to get your
                    belongings safely from door to door, anywhere
                    in the UK.
                  </ServiceDescription>
                </ServiceCard>

                {/* FURNITURE MOVES */}
                <ServiceCard whileHover={{scale:1.05}}
                  initial={{opacity:0,y:30}}
                  whileInView={{opacity:1,y:0}}
                  viewport={{once:true}}
                  transition={{delay:0.5}}
                  style={{textDecoration:'none',color:'inherit'}}
                >
                  <ServiceIcon
                    style={{
                      background: 'transparent',
                      padding: 0,
                      overflow: 'hidden',
                      width: '85%',
                      height: '140px',
                      margin: '0 auto 0.5rem auto'
                    }}
                  >
                    <img
                      src="/sofa_move.webp"
                      alt="Furniture moving service"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </ServiceIcon>
                  <ServiceTitle>Furniture Moves</ServiceTitle>
                  <ServiceDescription>
                    Bought a new sofa, bed or wardrobe?
                    Whether it's a single item or multiple
                    pieces of furniture, we'll handle collection
                    and delivery with care, making furniture
                    transport simple and hassle-free.
                  </ServiceDescription>
                </ServiceCard>
              </ServicesGrid>
            </Section>



            

            <HowItWorksSection id="how-it-works">
              <HowItWorksContainer>
                <SectionTitle initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} style={{color:'#1a1a1a'}}>How It Works</SectionTitle>
                

                <StepsGrid>
                  <StepCard style={{ boxShadow: "none" }} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}}>
                    <StepNumber style={{ boxShadow: "none" }}>1</StepNumber><StepH3>Request a Quote</StepH3>
                    <StepDescription>Share your shipping details and get an instant quote.</StepDescription>
                  </StepCard>
                  <StepCard style={{ boxShadow: "none" }} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.1}}>
                    <StepNumber style={{ boxShadow: "none" }}>2</StepNumber><StepH3>Schedule Pickup</StepH3>
                    <StepDescription>Choose a convenient pickup time from your location.</StepDescription>
                  </StepCard>
                  <StepCard style={{ boxShadow: "none" }} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.2}}>
                    <StepNumber style={{ boxShadow: "none" }}>3</StepNumber><StepH3>Stay Updated</StepH3>
                    <StepDescription>We'll keep you informed via email and phone at every stage.</StepDescription>
                  </StepCard>
                  <StepCard style={{ boxShadow: "none" }} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.3}}>
                    <StepNumber style={{ boxShadow: "none" }}>4</StepNumber><StepH3>Delivered</StepH3>
                    <StepDescription>Your shipment arrives safely and on time.</StepDescription>
                  </StepCard>
                </StepsGrid>
              </HowItWorksContainer>
            </HowItWorksSection>


            <CTASection>
              <CTAContent initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}}>
                <CTATitle>Ready to Move Your Goods?</CTATitle>
                <CTAText>Get a fast, free quote today. No commitment needed.</CTAText>
                <CTAButtonLarge
                  as={Link}
                  to="/get-a-quote"
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: '#0096D1'
                  }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    background: '#00A8E8',
                    color: '#FFFFFF',
                    border: '2px solid #00A8E8',
                    textDecoration: 'none'
                  }}
                >
                  Get Your Free Quote
                </CTAButtonLarge>
              </CTAContent>
            </CTASection>

            <Footer>
              <FooterContent className="responsive-footer">
                <FooterColumn>
                  <h4>Urgent Logistics</h4>
                  <p>Reliable courier, transport and removals services across the UK, including London and the South Coast. From business deliveries to house moves and furniture transport, Urgent Logistics offers flexible solutions to get your items moving safely and efficiently.</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Contact Info</h4>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Mail size={16}/> info@urgentservicelogistics.co.uk</p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Phone size={16}/><a href="tel:+447436117842" style={{color:'inherit',textDecoration:'none',lineHeight:'inherit'}}>07436 117842</a></p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><MapPinned size={16}/> Hampshire</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Operating Hours</h4>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Deliveries:</strong> 24 Hours, 7 Days</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Phone Support:</strong></p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Mon &ndash; Sat: 08:00 am &ndash; 20:00 pm</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Sun: 09:00 am &ndash; 21:00 pm</p>
                </FooterColumn>
              </FooterContent>
              <Copyright>
                © 2025 Urgent Logistics. All rights reserved. 
                Website by <a href="https://draftpoint.co.uk" target="_blank" rel="noopener noreferrer" style={{color:'#ff6b35',textDecoration:'none'}}>Draftpoint</a>
              </Copyright>
            </Footer>
          </>
        } />

        
                <Route path="/services" element={
          <>
            <Header />
            <ServicesPage />
          </>
        } />

        <Route path="/how-it-works" element={
          <>
            <Header />
            <HowItWorksPage />
          </>
        } />

                <Route path="/about" element={
          <>
            <Header />
            <AboutPage />
          </>
        } />

        <Route path="/get-a-quote" element={
          <>
            <Header />
            <QuoteRequest />
          </>
        } />

        <Route path="/quick-quote" element={
          <>
            <Header />
            <QuickQuote />
            <Footer>
              <FooterContent className="responsive-footer">
                <FooterColumn>
                  <h4>Urgent Logistics</h4>
                  <p>Reliable courier, transport and removals services across the UK, including London and the South Coast. From business deliveries to house moves and furniture transport, Urgent Logistics offers flexible solutions to get your items moving safely and efficiently.</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Contact Info</h4>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Mail size={16}/> info@urgentservicelogistics.co.uk</p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Phone size={16}/><a href="tel:+447436117842" style={{color:'inherit',textDecoration:'none',lineHeight:'inherit'}}>07436 117842</a></p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><MapPinned size={16}/> Hampshire</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Operating Hours</h4>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Deliveries:</strong> 24 Hours, 7 Days</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Phone Support:</strong></p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Mon &ndash; Sat: 08:00 am &ndash; 20:00 pm</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Sun: 09:00 am &ndash; 21:00 pm</p>
                </FooterColumn>
              </FooterContent>
              <Copyright>
                © 2025 Urgent Logistics. All rights reserved. 
                Website by <a href="https://draftpoint.co.uk" style={{color:'#ff6b35'}}>Draftpoint</a>
              </Copyright>
            </Footer>
          </>
        } />

        <Route path="/contact" element={
          <>
            <Header />
            <Contact />
            <Footer>
              <FooterContent className="responsive-footer">
                <FooterColumn>
                  <h4>Urgent Logistics</h4>
                  <p>Reliable courier, transport and removals services across the UK, including London and the South Coast. From business deliveries to house moves and furniture transport, Urgent Logistics offers flexible solutions to get your items moving safely and efficiently.</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Contact Info</h4>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Mail size={16}/> info@urgentservicelogistics.co.uk</p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Phone size={16}/><a href="tel:+447436117842" style={{color:'inherit',textDecoration:'none',lineHeight:'inherit'}}>07436 117842</a></p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><MapPinned size={16}/> Hampshire</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Operating Hours</h4>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Deliveries:</strong> 24 Hours, 7 Days</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Phone Support:</strong></p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Mon &ndash; Sat: 08:00 am &ndash; 20:00 pm</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Sun: 09:00 am &ndash; 21:00 pm</p>
                </FooterColumn>
              </FooterContent>
              <Copyright>
                © 2025 Urgent Logistics. All rights reserved. 
                Website by <a href="https://draftpoint.co.uk" style={{color:'#ff6b35'}}>Draftpoint</a>
              </Copyright>
            </Footer>
          </>
        } />

        <Route path="/man-with-van" element={
          <>
            <Header />
            <ManWithVan />
            <Footer>
              <FooterContent className="responsive-footer">
                <FooterColumn>
                  <h4>Urgent Logistics</h4>
                  <p>Reliable courier, transport and removals services across the UK, including London and the South Coast. From business deliveries to house moves and furniture transport, Urgent Logistics offers flexible solutions to get your items moving safely and efficiently.</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Contact Info</h4>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Mail size={16}/> info@urgentservicelogistics.co.uk</p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><Phone size={16}/><a href="tel:+447436117842" style={{color:'inherit',textDecoration:'none',lineHeight:'inherit'}}>07436 117842</a></p>
                  <p style={{display:'flex',alignItems:'center',gap:'8px',margin:'0 0 12px'}}><MapPinned size={16}/> Hampshire</p>
                </FooterColumn>
                <FooterColumn>
                  <h4>Operating Hours</h4>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Deliveries:</strong> 24 Hours, 7 Days</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}><strong>Phone Support:</strong></p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Mon &ndash; Sat: 08:00 am &ndash; 20:00 pm</p>
                  <p style={{margin:'0 0 8px', lineHeight:'1.6'}}>Sun: 09:00 am &ndash; 21:00 pm</p>
                </FooterColumn>
              </FooterContent>
              <Copyright>
                © 2025 Urgent Logistics. All rights reserved. 
                Website by <a href="https://draftpoint.co.uk" style={{color:'#ff6b35'}}>Draftpoint</a>
              </Copyright>
            </Footer>
          </>
        } />
      </Routes>
    </Container>
  );
}

export default App;